import { reactive } from 'vue'
import { CallApi } from '@/shared/api/methods/CallApi'
import { IntegrationApi } from '@/shared/api/methods/IntegrationApi'
import { CrmApi, isCrmUserInactive } from '@/shared/api/methods/CrmApi'

/** Гость, ждущий впуска в комнату (зал ожидания). */
export interface WaitingGuest {
  id: number
  name: string
}

// Общее состояние на всё приложение: текущая комната хоста и список ждущих.
const state = reactive({
  roomName: '' as string,
  guests: [] as WaitingGuest[],
})

// Сверка списка с сервером: сокет-событие могло не дойти, а впущенный или ушедший
// гость — остаться карточкой в панели.
const REFRESH_MS = 15000
let refreshTimer: ReturnType<typeof setInterval> | null = null
// Ведущий встречи интеграции без аккаунта: у него нет сокета, поэтому список
// ждущих он опрашивает чаще и управляет им по коду своей ссылки.
const HOST_LINK_REFRESH_MS = 4000
let hostCode = ''
// Ведущий встречи из CRM: тот же опрос, но по cookie сессии, а не по коду ссылки.
let hostSession = false
// Что делать, если сотрудника отключили в CRM: опрос — единственное, что окно
// ведущего спрашивает у сервера всю встречу, поэтому узнаёт об этом он.
let onInactive: (() => void) | null = null
// Заявки, по которым хост уже нажал «Впустить»/«Отклонить»: пока запрос летит,
// сверка не должна вернуть карточку обратно в список.
const decided = new Set<number>()

/**
 * Зал ожидания на стороне ХОСТА: список гостей, ждущих впуска, обновляется по
 * сокет-событию guest_knocking и первичной подгрузкой. Впуск/отказ — через API.
 */
export function useWaitingGuests() {
  async function setRoom(roomName: string, viaHostCode = '', viaHostSession = false, onCrmUserInactive?: () => void): Promise<void> {
    state.roomName = roomName || ''
    state.guests = []
    hostCode = viaHostCode
    hostSession = viaHostSession
    onInactive = viaHostSession ? onCrmUserInactive || null : null
    decided.clear()
    stopRefresh()
    if (!state.roomName) return
    await refresh()
    // Первый же опрос мог закрыть сессию (сотрудник отключён в CRM).
    if (!state.roomName) return
    refreshTimer = setInterval(() => { void refresh() }, hostCode || hostSession ? HOST_LINK_REFRESH_MS : REFRESH_MS)
  }

  function stopRefresh(): void {
    if (refreshTimer) { clearInterval(refreshTimer); refreshTimer = null }
  }

  async function refresh(): Promise<void> {
    if (!state.roomName) return
    try {
      const r: any = hostSession
        ? await CrmApi.hostWaiting()
        : hostCode
          ? await IntegrationApi.hostWaiting(hostCode)
          : await CallApi.waitingGuests(state.roomName)
      const list: WaitingGuest[] = Array.isArray(r?.data) ? r.data : []
      state.guests = list.filter((g) => !decided.has(g.id))
    } catch (e) {
      if (hostSession && isCrmUserInactive(e)) { inactive(); return }
      // сеть флапнула — обновимся по следующему событию
    }
  }

  // Сессия ведущего отозвана: опрос больше не нужен, решает окно встречи.
  function inactive(): void {
    const handler = onInactive
    clear()
    handler?.()
  }

  // Пришло сокет-событие «гость стучится».
  function onKnock(payload: { room_name?: string; admission_id?: number; guest_name?: string }): void {
    if (!payload?.admission_id || payload.room_name !== state.roomName) return
    if (decided.has(payload.admission_id)) return
    if (!state.guests.some((g) => g.id === payload.admission_id)) {
      state.guests.push({ id: payload.admission_id, name: payload.guest_name || 'Гость' })
    }
  }

  async function admit(id: number): Promise<void> {
    decided.add(id)
    remove(id)
    try {
      if (hostSession) await CrmApi.hostAdmit(id)
      else if (hostCode) await IntegrationApi.hostAdmit(hostCode, id)
      else await CallApi.admitGuest(id)
    } catch (e) {
      if (hostSession && isCrmUserInactive(e)) inactive()
      // иначе гость уже мог выйти
    }
  }
  async function deny(id: number): Promise<void> {
    decided.add(id)
    remove(id)
    try {
      if (hostSession) await CrmApi.hostDeny(id)
      else if (hostCode) await IntegrationApi.hostDeny(hostCode, id)
      else await CallApi.denyGuest(id)
    } catch (e) {
      if (hostSession && isCrmUserInactive(e)) inactive()
      // иначе гость уже мог выйти
    }
  }
  function remove(id: number): void {
    state.guests = state.guests.filter((g) => g.id !== id)
  }
  function clear(): void {
    stopRefresh()
    decided.clear()
    hostCode = ''
    hostSession = false
    onInactive = null
    state.roomName = ''
    state.guests = []
  }

  return { state, setRoom, refresh, onKnock, admit, deny, clear }
}
